import { memo, useCallback } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import AutoplayVideo from "components/AutoplayVideo";
import Carousel from "components/Carousel";
import { CarouselNextButton, CarouselSlide, CarouselStepper } from "components/Carousel/components";
import useCarousel from "components/Carousel/hooks/useCarousel";
import DialogContent from "components/DialogV2/DialogContent";
import Typography from "components/Typography";
import type { TypographyProps } from "components/Typography";

export const AthleteVelocityAnnouncementContent = styled(DialogContent)({
  "&[data-size=md]": {
    padding: 0,
  },
});

export const AthleteVelocityAnnouncementCarousel = styled(Carousel)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4.5),
  paddingBottom: theme.spacing(4.5),
}));

export const AthleteVelocityAnnouncementFooter = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
}));

export const AthleteVelocityAnnouncementMockup = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "stretch",
  gap: theme.spacing(1.5),
  padding: theme.spacing(5, 3, 3),
  backgroundColor: theme.palette.grey[20],
}));

export const AthleteVelocityAnnouncementVideo = styled(AutoplayVideo)({
  width: "100%",
  maxWidth: "540px",
  borderRadius: "8px",
});

export const AthleteVelocityAnnouncementText = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0, 4.5),
  textAlign: "center",
}));

/** Sits under the dialog title, so it is the third heading level. */
export function AthleteVelocityAnnouncementHeading(props: TypographyProps) {
  return <Typography variant="TextXXL" component="h3" fontWeight="fontWeightMedium" {...props} />;
}

const AthleteVelocityAnnouncementSlideRoot = styled(CarouselSlide)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4.5),
}));

interface AthleteVelocityAnnouncementSlideProps {
  video: string;
  videoAlt: string;
  heading: string;
  /** @description The description paragraphs. */
  children: ReactNode;
}

export const AthleteVelocityAnnouncementSlide = memo((props: AthleteVelocityAnnouncementSlideProps) => {
  return (
    <AthleteVelocityAnnouncementSlideRoot>
      <AthleteVelocityAnnouncementMockup>
        <AthleteVelocityAnnouncementVideo src={props.video} alt={props.videoAlt} />
        <CarouselStepper />
      </AthleteVelocityAnnouncementMockup>
      <AthleteVelocityAnnouncementText>
        <AthleteVelocityAnnouncementHeading>{props.heading}</AthleteVelocityAnnouncementHeading>
        <div>{props.children}</div>
      </AthleteVelocityAnnouncementText>
    </AthleteVelocityAnnouncementSlideRoot>
  );
});

const AthleteVelocityAnnouncementNextButtonRoot = styled(CarouselNextButton)(({ theme }) => ({
  minWidth: "142px",
}));

interface AthleteVelocityAnnouncementNextButtonProps {
  /** @description Called instead of moving on when the last slide is showing. */
  onFinish: () => void;
}

/**
 * Stays the same element on the last slide, reading "Got it", so keyboard focus is not lost
 * when the label changes.
 */
export const AthleteVelocityAnnouncementNextButton = memo((props: AthleteVelocityAnnouncementNextButtonProps) => {
  const { onFinish } = props;
  const { t } = useTranslation();
  const { isLastStep } = useCarousel();

  const handleOnClick = useCallback(() => {
    if (isLastStep) onFinish();
  }, [isLastStep, onFinish]);

  return (
    <AthleteVelocityAnnouncementNextButtonRoot onClick={handleOnClick} size="large">
      {isLastStep ? t("global.got_it_label") : t("global.next_button_text")}
    </AthleteVelocityAnnouncementNextButtonRoot>
  );
});
